import type { SpeciesItem } from '../api'
import { speciesName, useLang, useT } from '../i18n'
import { orphanSpecies } from '../species/orphanSpecies'
import { speciesGroups } from '../species/speciesGroups'

interface SpeciesSelectorProps {
  species: SpeciesItem[]
  value: number | null
  onChange: (id: number | null) => void
  /** Разрешить явный пункт «Все виды» (value=null — валидный выбор, не заглушка).
   *  Для режима дрейфа: без вида показываем маркеры всех видов. */
  allowAll?: boolean
  /** Виды, которых в этом кадре не бывает. Сюда попадает РОВНО один случай —
   *  выбранный вид вне своего района. Он не исчезает из списка: расчёт
   *  продолжает считать его, а список лишь честно говорит, что здесь этот вид
   *  не водится. Иначе залёт карты через границу стирал бы рабочий контекст. */
  outOfView?: SpeciesItem[]
}

export function SpeciesSelector({
  species, value, onChange, allowAll = false, outOfView = [],
}: SpeciesSelectorProps) {
  const t = useT()
  const { lang } = useLang()
  const groups = speciesGroups(species, outOfView)
  // Вид выбран, а справочник его не знает (не приехал, урезан правами, снят с
  // витрины). Без своего пункта `<select>` показал бы ПЕРВЫЙ — «Все виды» — и
  // подпись соврала бы поверх карты, которая рисует именно этот вид.
  const orphan = orphanSpecies(value, species, outOfView)
  const option = (s: SpeciesItem) => (
    <option key={s.species_id} value={s.species_id}>
      {speciesName(s, lang)}
    </option>
  )
  return (
    <select
      value={value ?? ''}
      onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
      className="w-full rounded-lg border border-hull bg-trench px-2.5 py-2 text-sm text-foam focus:border-sonar focus:outline-none"
    >
      <option value="" disabled={!allowAll}>
        {allowAll ? t('models.species.all') : t('models.species.pick')}
      </option>
      {orphan != null && (
        <option value={orphan}>{t('models.species.unknown', { id: orphan })}</option>
      )}
      {groups.map(g =>
        g.labelKey === null
          ? g.items.map(option)
          : (
            <optgroup key={g.labelKey} label={t(g.labelKey)}>
              {g.items.map(option)}
            </optgroup>
          ),
      )}
    </select>
  )
}
